import { useCallback, useState } from 'react';

import { Dropdown, StorageKey, StorageService } from '@treezor/design-system';
import { useTranslation } from 'react-i18next';

import flagFr from '@/assets/flags/fr.png';
import flagDe from '@/assets/flags/de.png';
import flagGb from '@/assets/flags/gb.png';
import flagEs from '@/assets/flags/es.png';

import i18n from '@/i18n';
import { useToggleDropdown } from '@/hooks/useToggleDropdown';

const languageList = {
  enGB: {
    label: 'English',
    path: flagGb,
  },
  fr: {
    label: 'Français',
    path: flagFr,
  },
  de: {
    label: 'Deutsch',
    path: flagDe,
  },
  es: {
    label: 'Español',
    path: flagEs,
  },
};

export function LanguageMenu() {
  const currentLang = StorageService.get(StorageKey.LANGUAGE);
  const [selectedLanguage, setSelectedLanguage] = useState<string>(currentLang ?? 'FR');
  const { t } = useTranslation();
  const { ref, isOpen: isMenuOpen, setOpen } = useToggleDropdown();

  const handleLanguageChange = useCallback(
    (lang: string) => {
      if (lang !== currentLang) i18n.changeLanguage(lang);
      setSelectedLanguage(lang);
      setOpen(false);
    },
    [setOpen, currentLang],
  );

  const imageClassName =
    'rounded-full duration-200 cursor-pointer w-[24px] h-[24px] p-[1px] bg-interactive border-transparent group-hover:border-interactive border hover:outline hover:outline-4 outline-interactive';

  return (
    <div className="relative">
      <div className="flex justify-between items-center group" onClick={() => setOpen((open) => !open)} ref={ref}>
        <img className={imageClassName} src={languageList[selectedLanguage]?.path} alt="language-menu" />
      </div>

      {isMenuOpen && (
        <Dropdown onClose={() => setOpen(false)} parentRef={ref}>
          <div className="flex flex-col">
            <div className="px-4 py-3 flex items-center justify-start">
              <span className="typo-subtitle-subtitle3 text-tertiary">
                {t('components.molecules.languageMenu.title')}
              </span>
            </div>

            {Object.entries(languageList).map(([key, item]) => (
              <div
                className="cursor-pointer flex items-center gap-3 py-[6px] px-4 group w-full data-[active=true]:text-interactive hover:text-interactive hover:bg-interactive"
                key={`language-item-${key}`}
                onClick={() => handleLanguageChange(key)}
              >
                <img className={imageClassName} src={item?.path} alt={item.label} />
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </Dropdown>
      )}
    </div>
  );
}
